import * as React from 'react';
import Link from 'components/Link';
import { getIsNativePlatform } from 'utils/client/mobile';
import { AiOutlineDeploymentUnit, AiFillStar, AiOutlineStar } from 'react-icons/ai';
import { FaDownload } from 'react-icons/fa';
import { BsBarChartSteps } from 'react-icons/bs';
import { TbCalendarDollar } from 'react-icons/tb';
import { Switch } from '@headlessui/react';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import abbreviateNumber from 'utils/shared/number/abbreviateNumber';
import { getSectorShortName } from 'utils/shared/trades/sectors';
import { getEarningsTooltip } from 'utils/shared/earnings';
import { trackEvent } from 'services/client/analytics';
import { DOWNLOAD_AGGREGATE } from 'services/client/analytics/events';
import { TICKER_FLOW_PAGE, OPTION_FLOW_PAGE } from 'constants/pages';
import { SortDirection } from 'utils/shared/trades/types';
import { convertToCSV, downloadCSV } from 'utils/client/csvUtils';
import { formatOptionFlowAggregateCsvData } from 'utils/shared/trades/formatOptionFlowAggregateCsvData';
import SectionTitle from 'components/SectionTitle';
import DarkPoolLevels from 'components/DarkPoolLevels';
import {
  TableWrapper,
  TableCell,
  TableHeader,
  TableBody,
  HeaderCellWrapper,
  HeaderCell,
  SentimentText,
  HeaderIcon,
  IconSortDesc,
  IconSortAsc,
  WatchListIcon,
} from 'components/Table';
import classNames from 'styles/utils/classNames';
import SubscribeModal from 'components/SubscribeModal';
import { useEarningsData } from 'hooks/useEarningsQuery';
import { useWatchlistEarningsFilter } from 'hooks/useWatchlistEarningsFilter';
import TooltipComponent from 'components/Tooltip';
import DelayedFlowNotice from 'components/DelayedFlowNotice';
import {
  getNextTickerAggregateSortState,
  getTickerAggregateSortKeyForMetricMode,
} from 'utils/shared/trades/sortTickerAggregates';
import {
  OverviewProps,
  OverviewHeaderProps,
  TickerAggregatedDetails,
  DarkPoolAggregate,
  AGGREGATE_TABLE_HEADERS,
  NET_AGGREGATE_TABLE_HEADERS,
  WEEKLY_NET_AGGREGATE_TABLE_HEADERS,
  WEEKLY_AGGREGATE_TABLE_HEADERS,
  DEFAULT_AGGREGATE_KEY,
  DARKPOOL_AGGREGATE_HEADERS,
  DEFAULT_DARKPOOL_KEY,
} from './props';
import { getAggregateFlowSharePresentation } from './getAggregateFlowSharePresentation';

import { AggregateRow, DarkPoolGridRow, FeedTypeToggle } from './styles';

export enum FeedType {
  Options = 'OPTIONS',
  DarkPool = 'DARK_POOL',
}

type CsvValue = string | number | boolean | null | undefined;
type AggregateCsvRow = Record<string, CsvValue>;

const getCsvNumber = (item: AggregateCsvRow, key: string) => Number(item[key] || 0);
const getCsvString = (item: AggregateCsvRow, key: string) => String(item[key] || '');

const formatDarkPoolData = (data: AggregateCsvRow[]) =>
  data.map((item) => ({
    Ticker: getCsvString(item, 'Ticker'),
    Sector: getCsvString(item, 'Sector'),
    'Premium ($)': parseFloat(getCsvNumber(item, 'Premium').toFixed(2)),
    Volume: parseFloat(getCsvNumber(item, 'Volume').toFixed(2)),
    'Golden Trades': parseFloat(getCsvNumber(item, 'Golden Trades').toFixed(2)),
    'Late Buys': parseFloat(getCsvNumber(item, 'Late Buys').toFixed(2)),
    'Late Sells': parseFloat(getCsvNumber(item, 'Late Sells').toFixed(2)),
    'Lit Volume': parseFloat(getCsvNumber(item, 'Lit Volume').toFixed(2)),
    'Average Daily Volume': parseFloat(getCsvNumber(item, 'Average Daily Volume').toFixed(2)),
  }));

const INJECTED_SCREENSHOT_RENDER_LIMIT = 100;
// Controlled header wrapping for cramped columns. Non-breaking spaces keep
// compact suffixes together so labels break cleanly on smaller screens instead
// of fragmenting. Keyed by the canonical header name so CSV and sort logic keep
// using the plain name.
const HEADER_DISPLAY_NAMES: Record<string, string> = {
  'Flow Sentiment %': 'Flow Sentiment\u00A0%',
  'Net Premium %': 'Net Premium\u00A0%',
  'Unusual Net Contracts': 'Unusual Net\u00A0Contracts',
  'Unusual Net Premium': 'Unusual Net\u00A0Premium',
  'Sweep Net Contracts': 'Sweep Net\u00A0Contracts',
  'Sweep Net Premium': 'Sweep Net\u00A0Premium',
};
const formatAggregateNumber = (value?: number | null) => abbreviateNumber(Math.round(value || 0));
const formatSignedPercent = (value?: number | null) => {
  const rounded = Math.round(value || 0);
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
};

export const Header: React.FC<
  React.PropsWithChildren<
    OverviewHeaderProps<FeedType> & {
      feedType: FeedType;
      setFeedType: (feedType: FeedType) => void;
      hasAccess: boolean;
      setIsWatchListModalOpen?: (isOpen: boolean) => void;
      isEarningsOnly?: boolean;
      setIsEarningsOnly?: (value: boolean) => void;
      pageHref?: string;
      metricMode?: 'total' | 'net';
    }
  >
> = ({
  aggregateSortKey,
  setAggregateSortKey,
  aggregateSortDirection,
  setAggregateSortDirection,
  isWeekly,
  isWatchListOnly,
  setIsWatchListOnly,
  hasWatchListItems,
  feedType,
  setFeedType,
  data,
  hasAccess,
  setIsWatchListModalOpen,
  isEarningsOnly,
  setIsEarningsOnly,
  pageHref,
  metricMode = 'total',
}) => {
  const isOptions = feedType === FeedType.Options;
  const optionHeaders =
    metricMode === 'net'
      ? isWeekly
        ? WEEKLY_NET_AGGREGATE_TABLE_HEADERS
        : NET_AGGREGATE_TABLE_HEADERS
      : isWeekly
      ? WEEKLY_AGGREGATE_TABLE_HEADERS
      : AGGREGATE_TABLE_HEADERS;
  const TITLE_NAMES = isOptions ? optionHeaders : DARKPOOL_AGGREGATE_HEADERS;
  const RowComponent = isOptions ? AggregateRow : DarkPoolGridRow;
  const cols = TITLE_NAMES.length - 1;
  const defaultSortKey = isOptions
    ? metricMode === 'net'
      ? 'netPremium'
      : DEFAULT_AGGREGATE_KEY
    : DEFAULT_DARKPOOL_KEY;
  const activeAggregateSortKey = isOptions
    ? getTickerAggregateSortKeyForMetricMode(aggregateSortKey, metricMode)
    : aggregateSortKey;

  const [isDownloadModalOpen, setIsDownloadModalOpen] = React.useState(false);

  const handleDownload = (): void => {
    trackEvent(DOWNLOAD_AGGREGATE, { hasAccess });

    if (!hasAccess) {
      return setIsDownloadModalOpen(true);
    }

    const formattedData = isOptions
      ? formatOptionFlowAggregateCsvData(data as TickerAggregatedDetails[], { isWeekly })
      : formatDarkPoolData(
          (data as DarkPoolAggregate[]).map((item) => {
            const obj: AggregateCsvRow = {};
            TITLE_NAMES.forEach((column) => {
              const value = item[column.sortKey as keyof typeof item];
              obj[column.name] = Array.isArray(value) ? value.join(', ') : value;
            });
            return obj;
          }),
        );
    const csv = convertToCSV(formattedData);
    downloadCSV(csv, 'Smart Market Insight by Ticker');
  };

  const handleOpenWatchListModal = () => {
    setIsWatchListModalOpen?.(true);
  };

  return (
    <>
      <TableHeader>
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center">
            <SectionTitle
              Icon={AiOutlineDeploymentUnit}
              title="Smart Market Insight by Ticker"
              style={{ marginBottom: 0 }}
            />
            {!isWeekly && (
              <div className="ml-4 flex items-center">
                <FeedTypeToggle
                  isActive={feedType === FeedType.Options}
                  onClick={() => {
                    setFeedType(FeedType.Options);
                    setAggregateSortKey(DEFAULT_AGGREGATE_KEY);
                    setAggregateSortDirection(SortDirection.None);
                  }}
                >
                  Options
                </FeedTypeToggle>
                <FeedTypeToggle
                  isActive={feedType === FeedType.DarkPool}
                  onClick={() => {
                    setFeedType(FeedType.DarkPool);
                    setAggregateSortKey(DEFAULT_DARKPOOL_KEY);
                    setAggregateSortDirection(SortDirection.None);
                  }}
                >
                  Dark Pool
                </FeedTypeToggle>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Switch.Group as="div" className="flex items-center">
              <Switch
                checked={isWatchListOnly}
                onChange={setIsWatchListOnly}
                className="group relative inline-flex h-3 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
              >
                <span
                  aria-hidden="true"
                  className={`${isWatchListOnly ? 'bg-yellow-400' : 'bg-gray-600'}
                    pointer-events-none absolute mx-auto h-2 w-5 rounded-full transition-colors duration-200 ease-in-out
                  `}
                />
                <span
                  aria-hidden="true"
                  className={`${isWatchListOnly ? 'translate-x-3' : 'translate-x-0'} ${
                    isWatchListOnly ? 'bg-white' : 'bg-gray-300'
                  } pointer-events-none absolute left-0 inline-block h-3 w-3 transform rounded-full border border-gray-400 shadow ring-0 transition-transform duration-200 ease-in-out`}
                />
              </Switch>
              <div className="ml-2 text-xs">
                <TooltipComponent title="Update your watchlist" position="bottom">
                  <button
                    type="button"
                    onClick={handleOpenWatchListModal}
                    className="flex cursor-pointer items-center whitespace-nowrap rounded-[4px] border border-gray-300 bg-gray-50 px-0.5 py-0.5 text-xs font-semibold text-yellow-500 shadow-sm transition-colors hover:bg-gray-100 active:bg-gray-200 sm:px-1 sm:py-1"
                  >
                    <AiFillStar
                      aria-hidden="true"
                      className={`h-3 w-3 sm:h-auto sm:w-auto ${
                        isWatchListOnly ? 'text-yellow-400' : 'text-gray-600'
                      }`}
                    />
                  </button>
                </TooltipComponent>
              </div>
              {isWatchListOnly && !hasWatchListItems && (
                <Switch.Label as="span" className="ml-2 leading-none" style={{ lineHeight: 0 }}>
                  <span className="text-xs font-medium leading-none text-yellow-400">
                    Add items to your watchlist to appear in the feed
                  </span>
                </Switch.Label>
              )}
            </Switch.Group>
            {pageHref === OPTION_FLOW_PAGE && (
              <Switch.Group as="div" className="flex items-center">
                <Switch
                  checked={isEarningsOnly || false}
                  onChange={setIsEarningsOnly}
                  className="group relative inline-flex h-3 w-6 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none"
                >
                  <span
                    aria-hidden="true"
                    className={`${isEarningsOnly ? 'bg-graph-purple' : 'bg-gray-600'}
                    pointer-events-none absolute mx-auto h-2 w-5 rounded-full transition-colors duration-200 ease-in-out
                  `}
                  />
                  <span
                    aria-hidden="true"
                    className={`${isEarningsOnly ? 'translate-x-3' : 'translate-x-0'} ${
                      isEarningsOnly ? 'bg-white' : 'bg-gray-300'
                    } pointer-events-none absolute left-0 inline-block h-3 w-3 transform rounded-full border border-gray-400 shadow ring-0 transition-transform duration-200 ease-in-out`}
                  />
                </Switch>
                <div
                  className={`ml-0.5 text-xs ${
                    isEarningsOnly ? 'text-yellow-400' : 'text-gray-600'
                  }`}
                >
                  <div
                    className={`rounded-[4px] px-0.5 py-0.5 transition-all duration-300 sm:px-1 sm:py-1 ${
                      isEarningsOnly ? ' text-graph-purple shadow-lg' : ' text-slate-400'
                    }`}
                  >
                    <TooltipComponent title="Show tickers with upcoming earnings" position="bottom">
                      <TbCalendarDollar aria-hidden="true" className="h-5 w-5" />
                    </TooltipComponent>
                  </div>
                </div>
              </Switch.Group>
            )}
            {!getIsNativePlatform() && (
              <button
                className="flex h-6 items-center justify-center rounded-[4px] bg-white/10 px-2 text-[.65rem] shadow-sm"
                onClick={handleDownload}
              >
                <FaDownload />
                <span className="ml-2 text-[.6rem] font-semibold">Download</span>
              </button>
            )}
          </div>
        </div>
        <RowComponent removePad className="min-h-[2.5rem]">
          {TITLE_NAMES.map((t, i) => {
            const isActiveSort =
              t.sortKey === activeAggregateSortKey && aggregateSortDirection !== SortDirection.None;
            return (
              <HeaderCellWrapper
                key={`${feedType}-${t.name}`}
                isUnusual={isOptions && i >= cols - 5 && i < cols - 2}
                isSweep={isOptions && i >= cols - 2}
                isActiveSort={isActiveSort}
                maxLabelLines={isOptions ? 2 : undefined}
                onClick={() => {
                  if (i === cols && !isOptions) {
                    return;
                  }

                  const nextSortState = getNextTickerAggregateSortState({
                    currentSortKey: activeAggregateSortKey,
                    currentSortDirection: aggregateSortDirection,
                    selectedSortKey: t.sortKey,
                    defaultSortKey,
                  });

                  setAggregateSortKey(nextSortState.sortKey);
                  setAggregateSortDirection(nextSortState.sortDirection);
                }}
              >
                <HeaderCell>
                  <span className="cell-name">{HEADER_DISPLAY_NAMES[t.name] ?? t.name}</span>
                  {isActiveSort && aggregateSortDirection === SortDirection.Desc && (
                    <HeaderIcon>
                      <IconSortDesc />
                    </HeaderIcon>
                  )}
                  {isActiveSort && aggregateSortDirection === SortDirection.Asc && (
                    <HeaderIcon>
                      <IconSortAsc />
                    </HeaderIcon>
                  )}
                </HeaderCell>
              </HeaderCellWrapper>
            );
          })}
        </RowComponent>
      </TableHeader>
      <SubscribeModal isOpen={isDownloadModalOpen} onClose={() => setIsDownloadModalOpen(false)} />
    </>
  );
};

const Row: React.FC<
  React.PropsWithChildren<{
    ticker: TickerAggregatedDetails;
    isWeekly?: boolean;
    watchListTickers: string[];
    handleBookmark: (symbol: string) => void;
    earningsMap?: Record<string, any>;
    pageHref?: string;
    metricMode?: 'total' | 'net';
  }>
> = React.memo(
  ({ ticker, isWeekly, watchListTickers, handleBookmark, earningsMap, metricMode = 'total' }) => {
    const isWatchListTrade = !!ticker.ticker && watchListTickers.includes(ticker.ticker);
    const isNetMode = metricMode === 'net';
    const {
      value: shareValue,
      sentiment: shareType,
      tooltip: shareTooltip,
    } = getAggregateFlowSharePresentation(ticker, metricMode);

    const { hasEarnings, getDaysToEarnings, getEarningsData } = useEarningsData(
      earningsMap?.earnings,
    );

    const earningsData = getEarningsData(ticker.ticker);
    const daysToEarnings = getDaysToEarnings(ticker.ticker);
    const tickerHasEarnings = hasEarnings(ticker.ticker);
    return (
      <AggregateRow key={ticker.ticker} tableBodyRow>
        <TableCell className="relative">
          <WatchListIcon className="absolute z-30">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleBookmark(ticker.ticker);
              }}
              className={classNames(
                'z-30 transition-all duration-200 hover:scale-150 group-hover/row:opacity-100',
                isWatchListTrade
                  ? 'text-yellow-400 opacity-100'
                  : 'text-gray-400 opacity-70 hover:text-yellow-400 hover:opacity-100',
              )}
            >
              {isWatchListTrade ? (
                <AiFillStar className="transition-transform duration-200" />
              ) : (
                <AiOutlineStar className="cursor-pointer transition-transform duration-200" />
              )}
            </button>
          </WatchListIcon>
          <div className="relative inline-flex items-center">
            <Link href={`${TICKER_FLOW_PAGE}/${ticker.ticker}`}>
              <TooltipComponent title={ticker.tickerName || ''} position="right">
                {ticker.ticker}
              </TooltipComponent>
            </Link>
            {tickerHasEarnings && (
              <TooltipComponent
                title={getEarningsTooltip(earningsData, daysToEarnings)}
                position="right"
              >
                <sup className="-top-2 cursor-pointer">
                  <TbCalendarDollar
                    aria-hidden="true"
                    className="h-3 w-3 rounded-sm bg-palette-black-2 text-graph-purple"
                  />
                </sup>
              </TooltipComponent>
            )}
            <span className="ml-1.5 text-gray-500">{ticker.tradeCount}</span>
          </div>
        </TableCell>
        <TableCell>
          <TooltipComponent title={shareTooltip} position="right">
            <SentimentText type={shareType}>{formatSignedPercent(shareValue)}</SentimentText>
          </TooltipComponent>
        </TableCell>
        <TableCell>{Math.round(ticker.otm)}%</TableCell>
        <TableCell>{(isWeekly ? ticker.volumeGrowth : ticker.momentum).toFixed(1)}x</TableCell>
        <TableCell>
          {formatAggregateNumber(isNetMode ? ticker.netContracts : ticker.contracts)}
        </TableCell>
        <TableCell>
          ${formatAggregateNumber(isNetMode ? ticker.netPremium : ticker.premium)}
        </TableCell>
        <TableCell>${ticker.price.toFixed(2)}</TableCell>
        <TableCell>{Math.round(ticker.expiry)} days</TableCell>
        <TableCell>{getSectorShortName(ticker.sector || '')}</TableCell>
        <TableCell>{Math.round(ticker.unusualOtm)}%</TableCell>
        <TableCell>
          {formatAggregateNumber(isNetMode ? ticker.netUnusualContracts : ticker.unusualContracts)}
        </TableCell>
        <TableCell>
          ${formatAggregateNumber(isNetMode ? ticker.netUnusualPremium : ticker.unusualPremium)}
        </TableCell>
        <TableCell>{Math.round(ticker.sweepOtm)}%</TableCell>
        <TableCell>
          {formatAggregateNumber(isNetMode ? ticker.netSweepContracts : ticker.sweepContracts)}
        </TableCell>
        <TableCell>
          ${formatAggregateNumber(isNetMode ? ticker.netSweepPremium : ticker.sweepPremium)}
        </TableCell>
      </AggregateRow>
    );
  },
);

const DarkPoolRow: React.FC<
  React.PropsWithChildren<{
    ticker: DarkPoolAggregate;
    isWeekly?: boolean;
    watchListTickers: string[];
    setActiveLevelsTicker: (ticker: string) => void;
    handleBookmark: (symbol: string) => void;
    earningsMap?: Record<string, any>;
  }>
> = React.memo(
  ({ ticker, watchListTickers, setActiveLevelsTicker, handleBookmark, earningsMap }) => {
    const isWatchListTrade = !!ticker.ticker && watchListTickers.includes(ticker.ticker);

    const { hasEarnings, getDaysToEarnings, getEarningsData } = useEarningsData(
      earningsMap?.earnings,
    );

    const earningsData = getEarningsData(ticker.ticker);
    const daysToEarnings = getDaysToEarnings(ticker.ticker);
    const tickerHasEarnings = hasEarnings(ticker.ticker);

    return (
      <DarkPoolGridRow key={ticker.ticker} tableBodyRow>
        <TableCell className="relative">
          <WatchListIcon className="absolute z-30">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleBookmark(ticker.ticker);
              }}
              className={classNames(
                'z-30 transition-all duration-200 hover:scale-150 group-hover/row:opacity-100',
                isWatchListTrade
                  ? 'text-yellow-400 opacity-100'
                  : 'text-gray-400 opacity-70 hover:text-yellow-400 hover:opacity-100',
              )}
            >
              {isWatchListTrade ? (
                <AiFillStar className="transition-transform duration-200" />
              ) : (
                <AiOutlineStar className="cursor-pointer transition-transform duration-200" />
              )}
            </button>
          </WatchListIcon>
          <Link href={`${TICKER_FLOW_PAGE}/${ticker.ticker}`} className="display-inline z-100">
            <TooltipComponent title={ticker.tickerName || ''} position="right">
              {ticker.ticker}
            </TooltipComponent>
          </Link>
          {tickerHasEarnings && (
            <TooltipComponent
              title={getEarningsTooltip(earningsData, daysToEarnings)}
              position="right"
            >
              <sup className="-top-1.5 cursor-pointer">
                <TbCalendarDollar
                  aria-hidden="true"
                  className="h-3 w-3 cursor-pointer rounded-sm bg-palette-black-2 text-graph-purple"
                />
              </sup>
            </TooltipComponent>
          )}
        </TableCell>
        <TableCell>{getSectorShortName(ticker.sector || '')}</TableCell>
        <TableCell>${abbreviateNumber(ticker.premium)}</TableCell>
        <TableCell>{abbreviateNumber(ticker.volume)}</TableCell>
        <TableCell>{ticker.goldenTradeCount}</TableCell>
        <TableCell>{ticker.lateBuyCount}</TableCell>
        <TableCell>{ticker.lateSellCount}</TableCell>
        <TableCell>{abbreviateNumber(ticker.dayVolume)}</TableCell>
        <TableCell>{abbreviateNumber(ticker.avgVolume)}</TableCell>
        <TableCell>
          <button
            onClick={() => setActiveLevelsTicker(ticker.ticker || '')}
            style={{ marginLeft: '-6px' }}
            className="flex items-center rounded border border-button-color bg-transparent px-2 py-1 transition-colors duration-150 hover:bg-button-color"
          >
            <BsBarChartSteps className="mr-1 h-2 w-2" /> View
          </button>
        </TableCell>
      </DarkPoolGridRow>
    );
  },
);

const OptionAggregatedTable: React.FC<React.PropsWithChildren<OverviewProps>> = ({
  tradesByTicker,
  darkPoolByTicker,
  aggregateSortKey,
  setAggregateSortKey,
  aggregateSortDirection,
  setAggregateSortDirection,
  isWeekly,
  isWatchListOnly,
  setIsWatchListOnly,
  hasWatchListItems,
  watchListTickers,
  hasAccess,
  setIsWatchListModalOpen,
  handleBookmark,
  earningsMap,
  pageHref,
  metricMode = 'total',
  delayedFlowWait = null,
}) => {
  const AGGREGATE_ROW_HEIGHT_DESKTOP = 30;
  const AGGREGATE_ROW_HEIGHT_MOBILE = 30;
  const DARKPOOL_ROW_HEIGHT_DESKTOP = 40;
  const DARKPOOL_ROW_HEIGHT_MOBILE = 40;
  const MOBILE_BREAKPOINT = 768;
  const [feedType, setFeedType] = React.useState(FeedType.Options);
  const [activeLevelsTicker, setActiveLevelsTicker] = React.useState('');
  const [isEarningsOnly, setIsEarningsOnly] = React.useState(false);
  const { filterTickers } = useWatchlistEarningsFilter<TickerAggregatedDetails>(earningsMap);
  const { filterTickers: filterDarkPoolTickers } =
    useWatchlistEarningsFilter<DarkPoolAggregate>(earningsMap);
  const isInjectedScreenshotMode =
    typeof window !== 'undefined' && Boolean((window as any).injectedPageData);
  let darkPoolSortKey = DEFAULT_DARKPOOL_KEY;

  if (
    !aggregateSortKey ||
    aggregateSortKey === DEFAULT_DARKPOOL_KEY ||
    aggregateSortDirection === SortDirection.None
  ) {
    darkPoolSortKey = DEFAULT_DARKPOOL_KEY;
  } else {
    darkPoolSortKey = aggregateSortKey;
  }

  const processedTickers = React.useMemo(() => {
    const filtered = filterTickers(tradesByTicker, {
      isWatchListOnly,
      isEarningsOnly,
      watchListTickers,
    });

    const now = Date.now();
    const msPerDay = 1000 * 60 * 60 * 24;
    const withEarningsData = filtered.map((ticker) => {
      const earningsData = earningsMap?.earnings?.[ticker.ticker];
      return {
        ...ticker,
        earningsDate: earningsData?.d || null,
        daysToEarnings: earningsData?.d
          ? Math.ceil((new Date(earningsData.d).getTime() - now) / msPerDay)
          : null,
      };
    });

    if (aggregateSortKey === 'earningsDate') {
      withEarningsData.sort((a, b) => {
        const aDate = a.earningsDate ? new Date(a.earningsDate).getTime() : Infinity;
        const bDate = b.earningsDate ? new Date(b.earningsDate).getTime() : Infinity;

        if (aggregateSortDirection === SortDirection.Asc) {
          return aDate - bDate;
        } else {
          return bDate - aDate;
        }
      });
    }

    return withEarningsData;
  }, [
    tradesByTicker,
    earningsMap,
    isEarningsOnly,
    isWatchListOnly,
    watchListTickers,
    aggregateSortKey,
    aggregateSortDirection,
    filterTickers,
  ]);

  const processedDarkPoolTickers = React.useMemo(() => {
    const filtered = filterDarkPoolTickers(darkPoolByTicker, {
      isWatchListOnly,
      isEarningsOnly,
      watchListTickers,
    });

    const now = Date.now();
    const msPerDay = 1000 * 60 * 60 * 24;
    const withEarningsData = filtered.map((ticker) => {
      const earningsData = earningsMap?.earnings?.[ticker.ticker];
      return {
        ...ticker,
        earningsDate: earningsData?.d || null,
        daysToEarnings: earningsData?.d
          ? Math.ceil((new Date(earningsData.d).getTime() - now) / msPerDay)
          : null,
      };
    });

    if (aggregateSortKey === 'earningsDate') {
      withEarningsData.sort((a, b) => {
        const aDate = a.earningsDate ? new Date(a.earningsDate).getTime() : Infinity;
        const bDate = b.earningsDate ? new Date(b.earningsDate).getTime() : Infinity;

        if (aggregateSortDirection === SortDirection.Asc) {
          return aDate - bDate;
        } else {
          return bDate - aDate;
        }
      });
      return withEarningsData;
    }

    return withEarningsData.sort((a, b) => {
      let first = aggregateSortDirection === SortDirection.Asc ? a : b;
      let last = aggregateSortDirection === SortDirection.Asc ? b : a;

      // @ts-ignore
      if (first[darkPoolSortKey] < last[darkPoolSortKey]) {
        return -1;
      }
      // @ts-ignore
      if (first[darkPoolSortKey] > last[darkPoolSortKey]) {
        return 1;
      }
      return 0;
    });
  }, [
    darkPoolByTicker,
    earningsMap,
    isEarningsOnly,
    isWatchListOnly,
    watchListTickers,
    aggregateSortKey,
    aggregateSortDirection,
    darkPoolSortKey,
    darkPoolSortKey,
    filterDarkPoolTickers,
  ]);

  const RenderOptionsRow = React.useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const ticker = processedTickers[index];
      return (
        <div style={style}>
          <Row
            key={ticker.ticker}
            ticker={ticker}
            isWeekly={isWeekly}
            watchListTickers={watchListTickers}
            handleBookmark={handleBookmark ?? (() => {})}
            earningsMap={earningsMap}
            pageHref={pageHref}
            metricMode={metricMode}
          />
        </div>
      );
    },
    [
      processedTickers,
      isWeekly,
      watchListTickers,
      handleBookmark,
      earningsMap,
      pageHref,
      metricMode,
    ],
  );

  const RenderDarkPoolRow = React.useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const ticker = processedDarkPoolTickers[index];
      return (
        <div style={style}>
          <DarkPoolRow
            key={ticker.ticker}
            ticker={ticker}
            watchListTickers={watchListTickers}
            setActiveLevelsTicker={setActiveLevelsTicker}
            handleBookmark={handleBookmark ?? (() => {})}
            earningsMap={earningsMap}
          />
        </div>
      );
    },
    [
      processedDarkPoolTickers,
      watchListTickers,
      setActiveLevelsTicker,
      handleBookmark,
      earningsMap,
    ],
  );

  return (
    <>
      <TableWrapper className="flex h-full w-full flex-col overflow-hidden">
        <Header
          isWeekly={isWeekly}
          aggregateSortKey={aggregateSortKey}
          setAggregateSortKey={setAggregateSortKey}
          aggregateSortDirection={aggregateSortDirection}
          setAggregateSortDirection={setAggregateSortDirection}
          isWatchListOnly={isWatchListOnly}
          setIsWatchListOnly={setIsWatchListOnly}
          hasWatchListItems={hasWatchListItems}
          feedType={feedType}
          setFeedType={setFeedType}
          data={feedType === FeedType.Options ? processedTickers : processedDarkPoolTickers}
          hasAccess={hasAccess}
          setIsWatchListModalOpen={setIsWatchListModalOpen}
          isEarningsOnly={isEarningsOnly}
          setIsEarningsOnly={setIsEarningsOnly}
          pageHref={pageHref}
          metricMode={metricMode}
        />
        <TableBody className="h-full w-full !pb-0">
          {isInjectedScreenshotMode ? (
            <div className="h-full w-full overflow-y-auto overflow-x-hidden">
              {feedType === FeedType.Options
                ? processedTickers
                    .slice(0, INJECTED_SCREENSHOT_RENDER_LIMIT)
                    .map((ticker) => (
                      <Row
                        key={ticker.ticker}
                        ticker={ticker}
                        isWeekly={isWeekly}
                        watchListTickers={watchListTickers}
                        handleBookmark={handleBookmark ?? (() => {})}
                        earningsMap={earningsMap}
                        pageHref={pageHref}
                        metricMode={metricMode}
                      />
                    ))
                : processedDarkPoolTickers
                    .slice(0, INJECTED_SCREENSHOT_RENDER_LIMIT)
                    .map((ticker) => (
                      <DarkPoolRow
                        key={ticker.ticker}
                        ticker={ticker}
                        watchListTickers={watchListTickers}
                        setActiveLevelsTicker={setActiveLevelsTicker}
                        handleBookmark={handleBookmark ?? (() => {})}
                        earningsMap={earningsMap}
                      />
                    ))}
            </div>
          ) : feedType === FeedType.Options && delayedFlowWait && processedTickers.length === 0 ? (
            <DelayedFlowNotice variant="line" sessionDate={delayedFlowWait.sessionDate} />
          ) : (
            <div className="h-full w-full overflow-hidden">
              <AutoSizer>
                {({ width, height }) => {
                  const isMobile = width < MOBILE_BREAKPOINT;
                  const aggregateRowHeight = isMobile
                    ? AGGREGATE_ROW_HEIGHT_MOBILE
                    : AGGREGATE_ROW_HEIGHT_DESKTOP;
                  const darkPoolRowHeight = isMobile
                    ? DARKPOOL_ROW_HEIGHT_MOBILE
                    : DARKPOOL_ROW_HEIGHT_DESKTOP;

                  return feedType === FeedType.Options ? (
                    <FixedSizeList
                      height={height}
                      itemCount={processedTickers.length}
                      itemSize={aggregateRowHeight}
                      width={width}
                    >
                      {RenderOptionsRow}
                    </FixedSizeList>
                  ) : (
                    <FixedSizeList
                      height={height}
                      itemCount={processedDarkPoolTickers.length}
                      itemSize={darkPoolRowHeight}
                      width={width}
                    >
                      {RenderDarkPoolRow}
                    </FixedSizeList>
                  );
                }}
              </AutoSizer>
            </div>
          )}
        </TableBody>
      </TableWrapper>
      {!!activeLevelsTicker && (
        <DarkPoolLevels ticker={activeLevelsTicker} onClose={() => setActiveLevelsTicker('')} />
      )}
    </>
  );
};

export default React.memo(OptionAggregatedTable);
